/* @flow */
import React from 'react';
import { connect } from 'react-redux';
import { OrderedMap } from 'immutable';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { debounce, isEmpty } from 'lodash';

import { CHARACTER_LIMIT } from 'chat/components/MessageComposer';
import { flush, saveMessage } from 'reducers/roleBasedMessagingSearch';
import { getInitialAnnouncementTextForGroup } from 'lib/getInitialAnnouncementTextForGroup';
import { Message, TARGET_TYPES } from 'entities/Message';
import { ComposeMessageRecipientsInput } from 'chat/components/SplitComposer';
import { uploadFile } from 'lib/fileUploadHelper';
import {
  uploadRequest,
  loadThumbnail,
  MODAL_FILE_CONTEXT_KEY,
  removeFile,
} from 'reducers/fileUploads';
import AccountLinkingComposerSidebar from 'chat/components/AccountLinkingComposerSidebar';
import CurrentUser from 'auth/CurrentUser';
import DropZoneModal from 'components/DropZoneModal';
import EventTracking from 'lib/EventTracking';
import LayerTrigger from 'components/LayerTrigger';
import linkPreviewQuery from 'chat/queries/linkPreview.gql';
import MediaObject from 'components/MediaObject';
import RemindAPI from 'lib/RemindAPI';
import Row from 'components/Row';
import TextEntities from 'lib/TextEntities';
import TextWithGlyph from 'components/TextWithGlyph';
import Tooltip from 'components/Tooltip';
import uuid from 'uuid/v4';
import VisualComposer from 'chat/components/VisualComposer';

import type { Target } from 'entities/Message';
import type { UploadRequestData } from 'reducers/fileUploads';
import type { UIEventContext } from './ComposerContext';
import type { LinkPreview_linkPreview } from 'chat/queries/__generated__/LinkPreview';
import type { ContentSourcesContent_me_composer_contentSourcesContent_items_downloadPreview as DownloadPreview } from 'user/queries/__generated__/ContentSourcesContent';

type Props = {
  onRequestHide: () => void,
  initialTargets?: Target[],
  draftAnnouncement: ?Message,
  isEditingScheduledMessage: boolean,
  onBack: Function,
  onMessageSent?: Function,
  isUrgentMessage?: boolean,

  // Redux state
  targets: Target[],
  districtIsSelected: boolean,
  district: ?ComposerOrganization,
  fileUploads: OrderedMap<string, UploadRequestData>,
  savedMessage: string,

  // Redux dispatchers
  uploadRequest: (fileContextKey: string, filename: string, blob: File) => void,
  loadThumbnail: (fileContextKey: string, filename: string, thumbnail: string) => void,
  removeFile: (fileContextKey: string, filename: string) => void,
  onFlush: () => *,
  saveMessage: (message: string) => *,

  // Drag 'n drop props
  connectDropTarget: Function,
  isDraggingFile?: boolean,

  // UIEventContext from composerContext
  uiEventContext: UIEventContext,

  // sharesdk url from url params
  shareSdkBodyUrl?: string,
};

type State = {
  text: string,
  scheduledTime: ?Date,
  uuidForNewMessage: ?string,
  linkPreviews: OrderedMap<string, *>,
};

export const URGENT_STRING = 'URGENT: ';

export class ComposeMessage extends React.Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  state: State;

  constructor(props: Props) {
    super(props);
    this.state = {
      text: '',
      scheduledTime: (null: ?Date),
      uuidForNewMessage: uuid(),
      linkPreviews: new OrderedMap(),
    };

    const { draftAnnouncement } = props;
    if (draftAnnouncement) {
      Object.assign(this.state, {
        scheduledTime: draftAnnouncement.getSendAt(),
      });

      const body = draftAnnouncement.getBody();
      if (body) {
        Object.assign(this.state, {
          text: body,
        });
      }
      // override the random uuid with one of the initial message, if it has one
      if (draftAnnouncement.getUuid()) {
        Object.assign(this.state, { uuidForNewMessage: draftAnnouncement.getUuid() });
      }

      const linkPreviews = {};
      draftAnnouncement.getLinkPreviews().forEach(p => {
        linkPreviews[p.getUrl()] = {
          title: p.getTitle(),
          description: p.getDescription(),
          url: p.getUrl(),
          image: p.getImageUrl(),
          image_width: p.getImageWidth(),
          image_height: p.getImageHeight(),
          rich_content_type: p.getRichContentType(),
          rich_content_url: p.getRichContentUrl(),
        };
      });
      Object.assign(this.state, { linkPreviews: OrderedMap(linkPreviews) });
    }

    if (props.shareSdkBodyUrl) {
      this.getLinkPreviews([props.shareSdkBodyUrl])
        .then(() => (props.shareSdkBodyUrl = undefined))
        .catch(() => {
          /* do nothing */
        });
    }
  }

  async componentWillMount() {
    const { groupCode } = this.context.router.params;
    const { draftAnnouncement, initialTargets, savedMessage, isUrgentMessage } = this.props;

    if (!draftAnnouncement && initialTargets && !savedMessage.length) {
      const initialTarget = initialTargets.length ? initialTargets[0] : null;
      const initialText =
        groupCode && initialTarget
          ? await getInitialAnnouncementTextForGroup(groupCode, initialTarget)
          : '';
      // urgent messages start with 'Urgent: ' - see URGENT_STRING
      this.setState({ text: isUrgentMessage ? `${URGENT_STRING}${initialText}` : initialText });
    } else if (savedMessage.length) {
      this.setState({ text: savedMessage });
    } else if (isUrgentMessage && !draftAnnouncement) {
      this.setState({
        text: URGENT_STRING,
      });
    }
  }

  componentDidMount() {
    const { text } = this.state;
    if (text) {
      this.props.saveMessage(text);
    }
    EventTracking.trackKey('announcements.composer.view', {
      composer_version: 'web_modal',
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.text !== this.state.text) {
      this.props.saveMessage(this.state.text);
    }
  }

  removeAllFiles = () => {
    const { fileUploads } = this.props;
    fileUploads
      .keySeq()
      .forEach(filename => this.props.removeFile(MODAL_FILE_CONTEXT_KEY, filename));
  };

  clearReduxState = () => {
    this.removeAllFiles();
    this.props.onFlush();
  };

  debouncedGetLinkPreviews = debounce((urls: Array<string>) => this.getLinkPreviews(urls), 500);

  getLinkPreviews = (urls: string[]): Promise<void[]> =>
    Promise.all(urls.map((u: string): Promise<void> => this.getSingleLinkPreview(u, null, null)));

  getSingleLinkPreview = async (
    url: string,
    existingPreview: ?$Shape<LinkPreview_linkPreview>,
    downloadPreview: ?DownloadPreview
  ): Promise<void> => {
    try {
      const data = await RemindAPI.execute({
        query: linkPreviewQuery,
        variables: { ...(existingPreview || {}), url },
      });

      const { linkPreview } = data;
      if (linkPreview) {
        // this is to resolve a bug involving the link preview service returning the _entire_ pdf
        // will also resolve this on the service as well.
        const description = linkPreview.description && linkPreview.description.slice(0, 1024);

        const {
          title,
          imageUrl,
          imageWidth,
          imageHeight,
          richContentType,
          richContentUrl,
        } = linkPreview;

        const { text, linkPreviews } = this.state;
        const linksInText = TextEntities.parseUrls(text).map(entity =>
          text.substring(entity.offset, entity.offset + entity.length)
        );

        let newLinkPreviews = linkPreviews;
        if (linksInText.includes(url)) {
          newLinkPreviews = newLinkPreviews.set(url, {
            title,
            description,
            url,
            image: imageUrl,
            image_width: imageWidth,
            image_height: imageHeight,
            rich_content_type: richContentType,
            rich_content_url: richContentUrl,
            downloadPreview,
          });
        }

        this.setState({
          linkPreviews: newLinkPreviews,
        });
      }
    } catch (error) {
      console.error('could not get link preview', error);
    }
  };

  changeText = (text: string) => {
    const { linkPreviews } = this.state;

    // Get previews for links added to text
    const linksInText = TextEntities.parseUrls(text).map(entity =>
      text.substring(entity.offset, entity.offset + entity.length)
    );
    const newLinks = linksInText.filter(url => !linkPreviews.has(url));

    this.debouncedGetLinkPreviews(newLinks);

    // Remove previews for links that are removed from text
    const currentLinkedPreviews = linkPreviews.filter(content => linksInText.includes(content.url));

    this.props.saveMessage(text);

    this.setState({
      text,
      linkPreviews: currentLinkedPreviews,
    });
  };

  changeScheduledTime = (scheduledTime: ?Date) => {
    this.setState({ scheduledTime });
  };

  handleComposerSent = () => {
    const { targets, onMessageSent } = this.props;
    const { scheduledTime } = this.state;

    if (onMessageSent) {
      onMessageSent({ scheduledTime, targets });
    }

    // Get a new uuid in case another message is composed
    this.setState({ uuidForNewMessage: uuid() });
    this.clearReduxState();
  };

  renderDropzoneModal = (): ?React$Element<*> => {
    const { fileUploads, isDraggingFile } = this.props;
    if (!isDraggingFile) {
      return null;
    }

    return (
      <div>
        <DropZoneModal
          titleDrop="Drop a file here"
          isFileUploaded={!fileUploads.isEmpty()}
          isHovering
        />
      </div>
    );
  };

  addLinkedContent = (content: *) => {
    // This method adds content from the content browser.
    const { text, linkPreviews } = this.state;
    const { title, description, contentUrl, imageUrl, downloadPreview } = content;

    if (linkPreviews.has(contentUrl)) {
      return;
    }

    // Only add a space in front of the link if there's already text there
    const hasText = text.trim().length > 0;
    const conditionalSpace = hasText ? ' ' : '';
    this.setState(
      {
        text: `${text}${conditionalSpace}${contentUrl} `,
        // Set an empty object to stop the same content from being added
        // multiple times while the preview resolves
        linkPreviews: linkPreviews.set(contentUrl, {}),
      },
      () => {
        this.getSingleLinkPreview(
          contentUrl,
          {
            url: contentUrl,
            title,
            description,
            imageUrl,
          },
          downloadPreview
        );
      }
    );
  };

  removeLinkPreview = (preview: *) => {
    const { text, linkPreviews } = this.state;
    const { url } = preview;

    this.setState({
      // Try to remove the url with the default whitespace first
      text: text
        .replace(url + ' ', '')
        .replace(url, '')
        .trim(),
      linkPreviews: linkPreviews.remove(url),
    });
  };

  targetsContainGroupsOrOrgs = () => {
    const { targets } = this.props;
    if (targets) {
      for (const target of targets) {
        if (
          target.type === TARGET_TYPES.GROUP_TARGET ||
          target.type === TARGET_TYPES.ORGANIZATION_TARGET
        ) {
          return true;
        }
      }
    }
    return false;
  };

  characterLimit = () => {
    return this.targetsContainGroupsOrOrgs()
      ? CurrentUser.getUser().getAnnouncementCharactersLimit()
      : CHARACTER_LIMIT;
  };

  characterLimitWithFiles = () => {
    return this.targetsContainGroupsOrOrgs()
      ? CurrentUser.getUser().getAnnouncementCharactersLimitWithFiles()
      : CHARACTER_LIMIT;
  };

  renderSenderInfoText = (district: ?ComposerOrganization, uiEventContext: UIEventContext) => {
    if (district) {
      const districtUuid = district.uuid;
      return (
        <Row size="xsmall">
          <MediaObject figure="right">
            <div />
            <LayerTrigger
              layer={
                <Tooltip>Recipients aren’t able to reply to messages sent from a district</Tooltip>
              }
              placement="top"
              alignment="middle"
              onShow={() =>
                EventTracking.trackKey('announcements.composer.sending_as.click', {
                  ...uiEventContext,
                  organization_uuid: districtUuid,
                })
              }>
              <span>
                <TextWithGlyph
                  color="thunder"
                  size="xxsmall"
                  glyph="help"
                  glyphColor="thunder"
                  glyphSize="small">
                  Sending as {district.name}
                </TextWithGlyph>
              </span>
            </LayerTrigger>
          </MediaObject>
        </Row>
      );
    }
    return null;
  };

  render() {
    const {
      targets,
      connectDropTarget,
      initialTargets,
      draftAnnouncement,
      districtIsSelected,
      district,
      isEditingScheduledMessage,
      isUrgentMessage,
      uiEventContext,
    } = this.props;

    const { text, scheduledTime, uuidForNewMessage, linkPreviews } = this.state;
    const autoFocusComposer = initialTargets != null;
    const sidebar = <AccountLinkingComposerSidebar addLinkedContent={this.addLinkedContent} />;

    return connectDropTarget(
      <div>
        {this.renderDropzoneModal()}
        <div className="ComposeMessage">
          <div className="ComposeMessage-inner">
            <div className="ComposeMessage-composer">
              {districtIsSelected && this.renderSenderInfoText(district, uiEventContext)}
              <ComposeMessageRecipientsInput onClick={this.props.onBack} />
              <VisualComposer
                autoFocus={autoFocusComposer}
                initialMessage={draftAnnouncement}
                className="ComposeMessage-composer-container"
                onMessageSent={this.handleComposerSent}
                charLimit={this.characterLimit()}
                charLimitWithFiles={this.characterLimitWithFiles()}
                districtIsSelected={districtIsSelected}
                targets={targets}
                isEditingScheduledMessage={isEditingScheduledMessage}
                onTextChange={this.changeText}
                uuidForNewMessage={uuidForNewMessage}
                onScheduledTimeChange={this.changeScheduledTime}
                scheduledTime={scheduledTime}
                text={text}
                linkPreviews={linkPreviews.filter(v => !isEmpty(v))}
                onRemoveLinkPreview={this.removeLinkPreview}
                onRequestHide={this.props.onRequestHide}
                orgUuid={district ? district.uuid : null}
                orgName={district ? district.name : null}
                onBack={this.props.onBack}
                isUrgentMessage={isUrgentMessage}
              />
            </div>
            <div className="ComposeMessage-sidebar">{sidebar}</div>
          </div>
        </div>
      </div>
    );
  }
}

const dndEvents = {
  drop: (props, monitor) => {
    const file = monitor.getItem().files[0];
    uploadFile(MODAL_FILE_CONTEXT_KEY, file, props.uploadRequest, props.loadThumbnail);
    EventTracking.trackKey(
      'announcements.announcement_modal.universal_composer.create_attachment.drag_and_drop',
      props.uiEventContext
    );
  },
  canDrop: props => {
    return props.fileUploads.isEmpty(); // can only attach one file at a time
  },
};

const collect = (connector, monitor) => ({
  connectDropTarget: connector.dropTarget(),
  isDraggingFile: monitor.isOver(),
});

const withDropTarget = DropTarget(NativeTypes.FILE, dndEvents, collect);

const mapDispatchToProps = (dispatch: Function) =>
  bindActionCreators(
    {
      uploadRequest,
      loadThumbnail,
      removeFile,
      onFlush: flush,
      saveMessage,
    },
    dispatch
  );

const mapStateToProps = (state: Object) => {
  const { roleBasedMessagingSearch, fileUploads } = state;
  const fileGroup = fileUploads.get(MODAL_FILE_CONTEXT_KEY);
  return {
    targets: roleBasedMessagingSearch.targets,
    savedMessage: roleBasedMessagingSearch.message,
    district: roleBasedMessagingSearch.districtIsSelected
      ? roleBasedMessagingSearch.selection[0].organization
      : null,
    districtIsSelected: roleBasedMessagingSearch.districtIsSelected,
    fileUploads: fileGroup || new OrderedMap(),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withDropTarget(ComposeMessage));
